import styled, { css } from 'styled-components';
import { ItemUser } from 'data/firestore/globals';
import Button from 'components/Button';
import {
    GREY_100,
    GREY_400,
    GREY_600,
    PURPLE_100,
    PURPLE_800,
} from '../../../../constants';

interface ItemProps {
    direction: 'outgoing' | 'incoming';
    role?: ItemUser['role'];
    isFirst?: boolean;
    isLast?: boolean;
    isIntermediate?: boolean;
}

export const ChatBubble = styled.div<ItemProps>`
    display: flex;
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    max-width: 70%;
    background: ${GREY_100};
    position: relative;
    color: ${GREY_600};
    font-size: 0.875rem;
    line-height: 1.5rem;
    font-weight: 400;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    white-space: pre-wrap;
    margin-top: ${(props) => (props.isFirst ? '0' : '0.125rem')};

    ${(props) => {
        if (props.isFirst && !props.isLast) {
            // First bubble: top corners rounded, bottom corners square
            return css`
                border-radius: ${props.direction === 'outgoing' ? '0.75rem 0rem 0 0.75rem' : '0rem 0.75rem 0.75rem 0'};
            `;
        }
        if (props.isIntermediate) {
            // Intermediate bubbles: no rounded corners
            return css`
                border-radius: ${props.direction === 'outgoing' ? '0.75rem 0rem 0 0.75rem' : '0rem 0.75rem 0.75rem 0'};
            `;
        }
        if (props.isLast && !props.isFirst) {
            // Last bubble: only bottom corners rounded
            return css`
                border-radius: ${props.direction === 'outgoing' ? '0.75rem 0 0.75rem 0.75rem' : '0rem 0.75rem 0.75rem 0.75rem'};
            `;
        }
        // Single bubble or default: all corners rounded
        return css`
            border-radius: ${props.direction === 'outgoing' ? '0.75rem 0rem 0.75rem 0.75rem' : '0rem 0.75rem 0.75rem 0.75rem'};
        `;
    }}

    ${(props) => props.role === 'agent' && css`
        background: ${PURPLE_100};
        color: ${PURPLE_800};
    `}

    ${(props) => props.role === 'agent' && props.isFirst && css`
        ::after {
            content: '';
            position: absolute;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0.75rem 0.785rem 0;
            border-color: ${PURPLE_100} transparent transparent transparent;
            left: 0;
            top: 0;
            transform: translateX(-50%);
        }
    `}

    ${(props) => props.direction === 'incoming' && props.role !== 'agent' && props.isFirst && css`
        ::after {
            content: '';
            position: absolute;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0.75rem 0.785rem 0;
            border-color: ${GREY_100} transparent transparent transparent;
            left: 0;
            top: 0;
            transform: translateX(-50%);
        }
    `}

    ${(props) => props.direction === 'outgoing' && props.isFirst && css`
        ::after {
            content: '';
            position: absolute;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0.75rem 0.785rem 0;
            border-color: ${props.role === 'agent' ? PURPLE_100 : GREY_100} transparent transparent transparent;
            top: 0;
            right: 0;
            transform: translateX(50%);
        }
    `}
`;

export const AuthorContainer = styled.div`
    padding: 6px 6px 6px 0;
    display: flex;
    justify-content: flex-end;
    gap: 6px;
`;

export const AuthorName = styled.p`
    text-align: end;
    font-size: 12px;
    color: ${GREY_400};
    font-family: 'Lato Bold', Arial, Helvetica, sans-serif;
`;

export const Time = styled.h3`
    color: ${GREY_400};
    font-size: 0.625rem;
    font-style: italic;
    font-weight: 400;
    line-height: 1.375rem;
`;

export const MessageContainer = styled.div<ItemProps>`
    padding: 0 16px;
    display: flex;
    flex-direction: row;
    gap: 1rem;

    ${(props) => props.direction === 'outgoing' && css`
        flex-direction: row-reverse;
    `};

    :last-of-type {
        padding-bottom: 16px;
    }
`;

export const MessageContent = styled.div<ItemProps>`
    display: flex;
    padding-bottom: 0.5rem;
    flex-direction: column;
    align-items: flex-end;
    flex: 1 0 0;
    max-width: 70%;

    ${(props) => props.direction === 'incoming' && css`
        align-items: flex-start;
    `};
`;

export const BubbleTopContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
`;

export const BubbleBottomContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    align-content: center;
    gap: 0.2rem;
    margin-top: 0.125rem;
`;

export const ToolCallsButton = styled(Button)`
    height: 1.25rem;
    padding: 0 0.5rem;
    font-size: 0.625rem;
    line-height: 1rem;
    min-height: 1.25rem;

    svg {
        width: 0.625rem;
        height: 0.625rem;
        margin-right: 0.25rem;
    }
`;

export const FromAd = styled.div`
    width: 100%;
`;

export const ReportButtonContainer = styled.div`
    display: flex;
    align-items: center;

    button {
        height: 1.25rem;
        padding: 0 0.5rem;
        font-size: 0.625rem;
        line-height: 1rem;
        min-height: 1.25rem;

        svg {
            width: 0.625rem;
            height: 0.625rem;
            margin-right: 0.25rem;
        }
    }
`;
